/**
 * Hook for managing async operations with consistent error handling.
 *
 * Eliminates the repetitive try-catch-finally pattern across components
 * by providing a reusable operation executor with loading state and error handling.
 */

import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { extractErrorMessage, TOAST_DURATIONS } from "./errorUtils";

export interface UseAsyncOperationOptions<T> {
  /** Called on successful operation with the result */
  onSuccess?: (data: T) => void;
  /** Called on error with the extracted error message */
  onError?: (error: string) => void;
  /** Show toast on error. Defaults to false. */
  showToast?: boolean;
  /** Fallback error message if extraction fails */
  fallbackMessage?: string;
  /** Toast duration for errors in ms. Defaults to TOAST_DURATIONS.error */
  toastDuration?: number;
}

export interface UseAsyncOperationResult<T> {
  /** Execute an async operation */
  execute: (operation: () => Promise<T>) => Promise<T | null>;
  /** Last successful result data */
  data: T | null;
  /** Whether an operation is currently in progress */
  isLoading: boolean;
  /** Current error message, if any */
  error: string | null;
  /** Manually set/clear the error state */
  setError: (error: string | null) => void;
  /** Clear error and reset state */
  reset: () => void;
}

/**
 * Hook for managing async operations with loading, error, and success states.
 *
 * @example
 * ```tsx
 * const { execute, isLoading, error } = useAsyncOperation<User>({
 *   showToast: true,
 *   onSuccess: (user) => navigate(`/users/${user.id}`),
 * });
 *
 * const handleSubmit = () => {
 *   execute(async () => {
 *     const response = await fetch('/api/users', { method: 'POST', body: data });
 *     if (!response.ok) throw new Error('Failed to create user');
 *     return response.json();
 *   });
 * };
 * ```
 */
export function useAsyncOperation<T>(
  options: UseAsyncOperationOptions<T> = {},
): UseAsyncOperationResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use ref to track options to avoid stale closures
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const execute = useCallback(
    async (operation: () => Promise<T>): Promise<T | null> => {
      const opts = optionsRef.current;

      setIsLoading(true);
      setError(null);

      try {
        const result = await operation();
        setData(result);
        opts.onSuccess?.(result);
        return result;
      } catch (err) {
        const message = extractErrorMessage(err, opts.fallbackMessage);
        setError(message);

        if (opts.showToast) {
          toast.error(message, {
            duration: opts.toastDuration ?? TOAST_DURATIONS.error,
          });
        }

        opts.onError?.(message);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    execute,
    data,
    isLoading,
    error,
    setError,
    reset,
  };
}

export default useAsyncOperation;
